// CHG-1278 / ITER-3403 — v2 "Use classic dashboard" fallback link.
//
// Renders inside ProfileMenu (additive — between Account settings and Sign
// out). Provides a one-click escape to v1 for any user who hits a v2 gap.
//
// Per project_alora_dashboard_v1_v2_coexistence.md: v1 stays canonical
// forever; v2 is the new surface. This link is the user's safety net.

import * as LucideIcons from "lucide-react";
import { type ComponentType } from "react";
import { v1ClassicUrlForHostname } from "../../lib/v1-host-bridge";

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
}>;

interface Props {
  /** Called after the user navigates away — the parent uses this to close
   * the popover. Defaults to no-op so tests can render the component
   * standalone without a popover context. */
  onNavigate?: () => void;
}

export function ClassicDashboardLink({ onNavigate }: Props) {
  // SSR-safe — hostname only available at runtime.
  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  const url = v1ClassicUrlForHostname(host);
  if (!url) return null;

  const { ExternalLink } = LucideIcons as unknown as { ExternalLink: IconCmp };

  return (
    <a
      href={url}
      role="menuitem"
      data-component="classic-dashboard-link"
      onClick={() => {
        // CHG-1287 — fire telemetry CustomEvent additively. AdoptionTelemetry
        // listens at window level; storage.ts uses the same pattern.
        try {
          window.dispatchEvent(
            new CustomEvent("v2-classic-fallback-clicked", {
              detail: { source: "profile-menu" },
            }),
          );
        } catch {
          // best-effort
        }
        onNavigate?.();
      }}
      title="Open the original v1 dashboard in this tab"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "8px 10px",
        textAlign: "left",
        border: "none",
        background: "transparent",
        color: "var(--color-text)",
        fontSize: 13,
        borderRadius: "var(--radius-sm, 6px)",
        cursor: "pointer",
        fontFamily: "inherit",
        textDecoration: "none",
        boxSizing: "border-box",
      }}
    >
      <ExternalLink size={14} aria-hidden="true" />
      <span style={{ flex: 1 }}>Use classic dashboard</span>
    </a>
  );
}
